import React, { useMemo } from 'react';
import { Card } from '../../CompositeComponents/Card/Card';
import type { HeatmapChartProps } from './types';

// ─── HeatmapChart ─────────────────────────────────────────────────────────────

function heatColor(pct: number): string {
  // 0 = lightest brand, 1 = darkest brand
  const stops = [
    { p: 0, r: 238, g: 242, b: 255 },
    { p: 0.25, r: 199, g: 210, b: 254 },
    { p: 0.5, r: 129, g: 140, b: 248 },
    { p: 0.75, r: 99, g: 102, b: 241 },
    { p: 1, r: 55, g: 48, b: 163 },
  ];
  const i = stops.findIndex((s) => s.p >= pct);
  if (i <= 0) return `rgb(${stops[0].r},${stops[0].g},${stops[0].b})`;
  const a = stops[i - 1];
  const b = stops[i];
  const t = (pct - a.p) / (b.p - a.p);
  const r = Math.round(a.r + (b.r - a.r) * t);
  const g = Math.round(a.g + (b.g - a.g) * t);
  const bl = Math.round(a.b + (b.b - a.b) * t);
  return `rgb(${r},${g},${bl})`;
}

export function HeatmapChart({ data, title, rowLabel, colLabel, className }: HeatmapChartProps) {
  const { rows, cols, maxVal, cellMap } = useMemo(() => {
    const rowSet = new Set<string>();
    const colSet = new Set<string>();
    let max = 0;
    const map: Record<string, number> = {};
    for (const cell of data) {
      rowSet.add(cell.row);
      colSet.add(cell.col);
      if (cell.value > max) max = cell.value;
      map[`${cell.row}__${cell.col}`] = cell.value;
    }
    return { rows: [...rowSet], cols: [...colSet], maxVal: max, cellMap: map };
  }, [data]);

  return (
    <Card className={className}>
      {title && (
        <p className="mb-3 text-sm font-semibold text-neutral-700 dark:text-neutral-200">{title}</p>
      )}

      <div className="overflow-x-auto">
        <table className="text-xs border-separate border-spacing-0.5" aria-label={title ?? 'Heatmap'}>
          <thead>
            <tr>
              {/* corner cell */}
              <th className="text-neutral-400 font-normal text-right pr-1 pb-1" aria-hidden="true">
                {rowLabel && <span className="sr-only">{rowLabel}</span>}
              </th>
              {cols.map((col) => (
                <th
                  key={col}
                  className="text-neutral-500 dark:text-neutral-400 font-medium text-center pb-1 min-w-[2rem]"
                  scope="col"
                >
                  {col}
                </th>
              ))}
            </tr>
            {colLabel && (
              <tr>
                <td />
                <td colSpan={cols.length} className="text-center text-neutral-400 text-xs pb-1">
                  {colLabel}
                </td>
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row}>
                <th
                  scope="row"
                  className="text-neutral-500 dark:text-neutral-400 font-medium text-right pr-2 whitespace-nowrap"
                >
                  {row}
                </th>
                {cols.map((col) => {
                  const val = cellMap[`${row}__${col}`] ?? 0;
                  const pct = maxVal > 0 ? val / maxVal : 0;
                  const bg = heatColor(pct);
                  const textColor = pct > 0.55 ? 'white' : '#374151';
                  return (
                    <td
                      key={col}
                      className="text-center rounded p-1 min-w-[2rem] h-8"
                      style={{ backgroundColor: bg, color: textColor }}
                      title={`${row} × ${col}: ${val.toLocaleString('en-IN')}`}
                    >
                      {val > 0 ? val.toLocaleString('en-IN') : ''}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rowLabel && (
        <p className="mt-1 text-xs text-neutral-400 text-right">{rowLabel} →</p>
      )}
    </Card>
  );
}
